import { 
    useRef, 
    useEffect, 
    useState
} from "react"
import { Text } from "../units/Text"
import { BoxWrapper, FlexWrapper } from "../Wrappers/Wrappers"
import {
    FetchDeviceList,
    CreateDevice,
    AxiosDeleteDevice,
    UpdateDevice
} from "../Rest/RestDevices"
import {
    DeviceType,
} from "../Rest/RestDevices"
import { UniversalFormInput, Input } from "../Form/FormInput"
import { RestApiState } from "../Rest/Organization"
import { SubmitButton } from "../Buttons/buttons"
import { useParams } from "react-router-dom"
import { useSignedInContext } from "../../context/AuthContextProvider"
import { hasPermission } from "../../roles/roles"


interface EditDevicesProps {
    deviceList: DeviceType[];
    deviceCopy: DeviceType | null;
    timeOutId: NodeJS.Timeout | null;
    requestState: RestApiState;
    delete : {
        timeOutId: null,
        requestState: RestApiState,
        code: null
    },
    update: {
        timeOutId: null,
        requestState: RestApiState,
        code: null
    }, 
    create: {
        timeOutId: null,
        requestState: RestApiState,
        code: null
    }
}


const defaultVals = ():EditDevicesProps => {
    return {
        deviceList: [],
        deviceCopy: null,
        timeOutId: null,
        requestState: RestApiState.Idle, 
        delete : {
            timeOutId: null,
            requestState: RestApiState.Idle,
            code: null
        },
        update: {
            timeOutId: null,
            requestState: RestApiState.Idle,
            code: null
        }, 
        create: {
            timeOutId: null,
            requestState: RestApiState.Idle,
            code: null
        }, 
    }

} 

const notNullString = (value: any): string => {
    if (value) {
        return value
    }
    throw new Error(`${value} Value is null`)
}

const notNullNumber = (value: any): number => {
    if (value) {
        return value
    }
    throw new Error(`${value} Value is null`)
}


export const EditDevices = () => {
    const createFormRef = useRef<HTMLFormElement | null>(null)
    const { user } = useSignedInContext()
    const { id } = useParams();
    const [props, setProps] = useState<EditDevicesProps>(defaultVals)
    
    if (!id) {
        throw new Error("ID must be provided")
    }

    const createDevice = async (event:any) => {
        event.preventDefault()
        const formData = new FormData(event.currentTarget)
        const formValues: Record<string, string> = {};

        formData.forEach((value, key) => {
            formValues[key] = value as any; // Cast value to string if necessary
        });

        const data = {
            device_name: notNullString(formData.get('device_name')),
            organization: id
        }

        setProps((prev:EditDevicesProps) => ({
            ...prev, create: {
                ...prev.create, requestState: RestApiState.Pending
            }
        }))


        CreateDevice({ role: user.role_slug,
            setStatus: (value: any) => {
                setProps((prev:EditDevicesProps) => ({
                    ...prev, create: {
                        ...prev.create, requestState: value
                    }
                }))
        
            }, 
            setValue: (value:any) => { setProps(prev => ({ ...prev, deviceList: [...prev.deviceList, value] }))
            }, 
            data: data })
        
    }


    useEffect(() => {
        FetchDeviceList({role: user.role_slug, organization_id: id, setter: (value:any) => {
            setProps(prev => ({ ...prev, 
                deviceList: [...prev.deviceList, ...value] 
            }))
        }})
    }, [])


    useEffect(() => {
        if (props.create.requestState === RestApiState.Success) {
            createFormRef.current?.reset();
        }

    }, [props.create.requestState])

    useEffect(() => {
        if (props.update.requestState === RestApiState.Success) {
            setProps((prev:EditDevicesProps) => ({
                ...prev, deviceCopy: null
            }))
        }

    }, [props])

    const updateDevice = async (event: any) => {
        event.preventDefault()
        const formData = new FormData(event.currentTarget)
        const formValues: Record<string, string> = {};

        formData.forEach((value, key) => {
            formValues[key] = value as any; // Cast value to string if necessary
        });

        const data = {
            device_name: notNullString(formData.get('device_name')),
            organization: id,
            id: notNullNumber(formData.get('id'))
        }

        setProps((prev:EditDevicesProps) => ({
            ...prev, update: {
                ...prev.update, requestState: RestApiState.Pending
            }
        }))

        UpdateDevice({ role: user.role_slug,
            setStatus: (value: any) => {
                setProps((prev:EditDevicesProps) => ({
                    ...prev, update: {
                        ...prev.update, requestState: value
                    }
                }))
            }, 
            setValue: (value:any) => {
                setProps((prev) => ({...prev, deviceList : prev.deviceList.map(device => device.id === value.id ? { ...device, ...value }: device)}))
            }, 
            device: data }
        )

    }

    const deleteDevice = async (item: DeviceType) => {
        AxiosDeleteDevice({ role: user.role_slug, 
            setStatus: (value: any) => {
                setProps((prev:EditDevicesProps) => ({
                    ...prev, delete: {
                        ...prev.delete, requestState: value
                    }
                }))
            }, 
            setValue: (value: number) => {
                setProps((prev) => ({...prev, deviceList : prev.deviceList.filter(device => device.id !== value) }))
            }, 
            id: item.id })
    }

    return (
        <BoxWrapper bg="bg-primary" r="r15" p="p4" className="">
            { hasPermission({role: user.role, resource: "Device", action: "create"}) && (
                <form onSubmit={createDevice} ref={createFormRef}>
                    <Text className="bold" size="xl">Добавить технику</Text>
                    <br />
                    <FlexWrapper flexWrap="nowrap" alignItems="end">
                        <UniversalFormInput label='Название' name='device_name' type='text' required={true} />
                        <SubmitButton 
                            text={"Добавить технику"}
                            restState={props.create.requestState}
                            disabled={props.create.requestState !== RestApiState.Idle}
                            restHandler={{
                                timeOutId: props.create.timeOutId,
                                setter: (key: 'requestState' | 'timeOutId', value: any) => {
                                    setProps((prev:EditDevicesProps) => ({
                                        ...prev, create: {
                                            ...prev.create, [key]: value
                                        }
                                    }))
                                }
                            }}
                            height="47px"
                            width="250px"
                            className="bold"
                            p="p3"
                        />
                    </FlexWrapper>
                    <br />
                    <br />
                </form>
            )}
            {props.deviceList.length > 0 && (
                <>
                    <Text className="bold" size="xl">Виды техники</Text>
                    <br />
                </>
            ) }
            
            { props.deviceList.length > 0 && (
                props.deviceList
                    .sort((a: DeviceType, b: DeviceType) => {
                        // Replace `propertyName` with the property you want to sort by
                        if (a.device_name < b.device_name) return -1; // Ascending order
                        if (a.device_name > b.device_name) return 1;  // Ascending order
                        return 0; // Equal values
                    })
                    .map((item:DeviceType, index:number) => {
                        const isSelected: boolean = item.id === props.deviceCopy?.id
                        return (
                            <BoxWrapper key={index} p="p-v-2" className="standart-border-bottom">
                                <form onSubmit={updateDevice}>
                                    <FlexWrapper flexWrap="nowrap" alignItems="center">
                                        <Input 
                                            value={!isSelected ? item.device_name : props.deviceCopy?.device_name} 
                                            setter={ (e:any) => { 
                                                setProps(
                                                    (prev:any) => ( {...prev, deviceCopy: { ...prev.deviceCopy, device_name: e.target.value } })
                                                )
                                             } }
                                            name='device_name' 
                                            type='text' 
                                            required={true} 
                                            disabled={ !isSelected } 
                                            height="cs-input-base" 
                                            className={ `${!isSelected ? 'cs-no-border' : ''}`} 
                                            showErros={false}

                                        />
                                        <input type="text" value={item.id} name="id" id="id" style={{display: 'none'}} disabled={false} />


                                        <BoxWrapper style={{width: "650px"}} p="p0">
                                            { isSelected ? (
                                                <FlexWrapper justifyContent="flex-end" alignItems="center" flexWrap="nowrap" className="narrow-gap">
                                                    <SubmitButton 
                                                        text={"Обновить"}
                                                        restState={props.update.requestState}
                                                        disabled={props.update.requestState !== RestApiState.Idle}
                                                        restHandler={{
                                                            timeOutId: props.update.timeOutId,
                                                            setter: (key: 'requestState' | 'timeOutId', value: any) => {
                                                                setProps((prev:EditDevicesProps) => ({
                                                                    ...prev, update: {
                                                                        ...prev.update, [key]: value
                                                                    }
                                                                }))
                                                            }
                                                        }}
                                                        height="36px"
                                                        width="100%"
                                                        className="bold"
                                                    />
                                                    <Text onClick={ (e:any) => {
                                                        setProps((prev:EditDevicesProps) => ({
                                                            ...prev, deviceCopy: null
                                                        }))
                                                    } } className="full-width text-center">Отменить</Text>
                                                </FlexWrapper>
                                                
                                            ) : (
                                                <FlexWrapper justifyContent="flex-end" alignItems="center">
                                                    { hasPermission({role: user.role, resource: "Building", action: "delete"}) && (
                                                        <svg onClick={(e:any) => {deleteDevice(item)}} width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" style={{cursor: "pointer"}}>
                                                            <path d="M18.5617 24.8792V18.2754C18.5617 17.6432 19.0954 17.1311 19.7544 17.1311C20.4135 17.1311 20.9472 17.6432 20.9472 18.2754V24.8792C20.9472 25.5114 20.4135 26.0234 19.7544 26.0234C19.0954 26.0234 18.5617 25.5114 18.5617 24.8792ZM13.394 26.0234C14.0531 26.0234 14.5868 25.5114 14.5868 24.8792V18.2754C14.5868 17.6432 14.0531 17.1311 13.394 17.1311C12.735 17.1311 12.2013 17.6432 12.2013 18.2754V24.8792C12.2013 25.5114 12.735 26.0234 13.394 26.0234ZM28.9 11.41C28.9 12.5971 28.2663 13.6455 27.3091 14.262V26.4054C27.3091 28.7196 25.3485 30.6004 22.9362 30.6004H10.2138C7.80147 30.6004 5.84086 28.7196 5.84086 26.4054V14.262C4.88367 13.6455 4.25 12.5971 4.25 11.41C4.25 9.51629 5.85426 7.97731 7.8283 7.97731H10.6119V7.59541C10.6119 5.28121 12.5725 3.40039 14.9849 3.40039H18.1651C20.5775 3.40039 22.5381 5.28121 22.5381 7.59541V7.97731H25.3217C27.2957 7.97731 28.9 9.51629 28.9 11.41ZM12.9959 7.97731H20.1525V7.59541C20.1525 6.54415 19.261 5.68885 18.1651 5.68885H14.9849C13.889 5.68885 12.9975 6.54415 12.9975 7.59541V7.97731H12.9959ZM24.9236 14.8427H8.22487V26.4053C8.22487 27.4566 9.11645 28.3119 10.2123 28.3119H22.9346C24.0305 28.3119 24.9221 27.4566 24.9221 26.4053V14.8427H24.9236ZM26.5145 11.41C26.5145 10.7778 25.9807 10.2658 25.3217 10.2658H7.82677C7.16776 10.2658 6.63401 10.7778 6.63401 11.41C6.63401 12.0422 7.16776 12.5542 7.82677 12.5542H25.3202C25.9792 12.5542 26.5129 12.0422 26.5129 11.41H26.5145Z" fill="#A8A8A8"/>
                                                        </svg>
                                                    ) }
                                                    { hasPermission({role: user.role, resource: "Building", action: "update"}) && (
                                                        <svg onClick={(e: any) => { 
                                                            setProps((prev:EditDevicesProps) => ({
                                                                ...prev, deviceCopy: item
                                                            }))
                                                            }} width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" style={{cursor: "pointer"}}>
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M13.2795 15.5953C12.2002 16.6745 11.5224 17.8478 11.1276 19.3231L9.81787 24.2105L14.692 22.9042C16.1751 22.5071 17.3539 21.8227 18.4377 20.7344L25.5566 13.5823L20.4093 8.46677L13.2793 15.5965L13.2795 15.5953ZM26.7748 17.425C26.7748 16.603 27.4414 15.9375 28.2624 15.9375C29.0844 15.9375 29.7499 16.603 29.7499 17.425V24.547C29.7499 27.882 27.0321 30.6 23.6967 30.6H9.45321C6.11696 30.6 3.40002 27.8823 3.40002 24.547V10.303C3.40002 6.96797 6.11787 4.25 9.45321 4.25H16.5755C17.3976 4.25 18.0631 4.91545 18.0631 5.73748C18.0631 6.55952 17.3965 7.22497 16.5755 7.22497H9.45321C7.75986 7.22497 6.37637 8.60955 6.37637 10.3017V24.5446C6.37637 26.2379 7.76101 27.6214 9.45321 27.6214H23.6967C25.3901 27.6214 26.7736 26.2368 26.7736 24.5446V17.4226L26.7748 17.425ZM27.3218 11.8095L22.1779 6.69741L23.5727 5.30275C24.4518 4.42368 25.6776 4.09485 26.815 4.31743C27.5252 4.45611 28.1527 4.78827 28.6973 5.33295C30.0798 6.7153 30.12 8.9992 28.7029 10.4227L27.3217 11.8107L27.3218 11.8095Z" fill="#A8A8A8"/>
                                                        </svg>
                                                    )}
                                                </FlexWrapper>
                                            )}
                                        </BoxWrapper>
                                        
                                    </FlexWrapper>
                                </form>
                            </BoxWrapper>
                        )}
                    )
            )}
        </BoxWrapper>
    )
}